import React, { useState, useEffect, useCallback } from 'react';
import { tenantApi, User, InviteUserRequest, Role, Group } from '../../services/tenantApi';

const TENANT_RESTRICTED_ROLES = new Set(['PlatformAdmin', 'PortalAdmin']);
const COMMON_TIMEZONES = [
  'UTC',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Pacific/Auckland',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Kolkata',
  'Europe/London',
  'Europe/Amsterdam',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Africa/Johannesburg',
  'Etc/GMT'
];

export const UsersManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<User | null>(null);
  const [search, setSearch] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await tenantApi.getUsers({ search: search || undefined });
      setUsers(result.users);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleInvite = async (data: InviteUserRequest) => {
    try {
      await tenantApi.inviteUser(data);
      setShowInviteModal(false);
      loadUsers();
    } catch (err: any) {
      throw err;
    }
  };

  const handleResendInvitation = async (userId: string) => {
    try {
      await tenantApi.resendInvitation(userId);
      alert('Invitation sent!');
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm(`Are you sure you want to delete ${user.displayName}?`)) return;
    try {
      await tenantApi.deleteUser(user.userId);
      loadUsers();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUpdateRoles = async (userId: string, roles: string[]) => {
    try {
      await tenantApi.assignRoles(userId, roles);
      setShowEditModal(null);
      loadUsers();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="management-section users-management-view">
      <div className="section-header">
        <h2>Users ({total})</h2>
        <button className="btn-primary" onClick={() => setShowInviteModal(true)}>
          + Invite User
        </button>
      </div>

      <div className="filters">
        <input
          type="search"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <table className="data-table users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Roles</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.userId}>
                <td>{user.displayName}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`status-badge ${user.status.toLowerCase().replace(' ', '-')}`}>
                    {user.status}
                  </span>
                </td>
                <td>{user.roles.join(', ') || 'None'}</td>
                <td>
                  <div className="users-actions-row">
                    <button className="btn-icon users-action-btn" title="Edit" onClick={() => setShowEditModal(user)}>✏️</button>
                    {user.status === 'PendingInvitation' && (
                      <button className="btn-icon users-action-btn" title="Resend Invitation" onClick={() => handleResendInvitation(user.userId)}>📧</button>
                    )}
                    <button className="btn-icon users-action-btn" title="Delete" onClick={() => handleDeleteUser(user)}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="empty-row">No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInvite}
        />
      )}

      {showEditModal && (
        <EditUserModal
          user={showEditModal}
          onClose={() => setShowEditModal(null)}
          onSave={handleUpdateRoles}
        />
      )}
    </div>
  );
};

const InviteUserModal: React.FC<{
  onClose: () => void;
  onInvite: (data: InviteUserRequest) => Promise<void>;
}> = ({ onClose, onInvite }) => {
  const [formData, setFormData] = useState<InviteUserRequest>({
    email: '',
    displayName: '',
    phone: '',
    timezone: 'UTC',
    roles: ['Viewer'],
    groupIds: [],
  });
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadInviteDefaults();
  }, []);

  const loadInviteDefaults = async () => {
    try {
      const [roles, groups, settings] = await Promise.all([
        tenantApi.getRoles(),
        tenantApi.getGroups(),
        tenantApi.getTenantSettings(),
      ]);

      setAvailableRoles((roles || []).filter((r) => !TENANT_RESTRICTED_ROLES.has(r.name)));
      setAvailableGroups(groups || []);

      const defaultRole = (settings.defaultNewUserRole || 'Viewer').trim() || 'Viewer';
      const defaultGroupId = (settings.defaultNewUserGroupId || '').trim();
      setFormData((prev) => ({
        ...prev,
        roles: [defaultRole],
        groupIds: defaultGroupId ? [defaultGroupId] : [],
      }));
    } catch (err) {
      console.error('Failed to load invite defaults:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onInvite(formData);
    } catch (err: any) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const toggleRole = (roleName: string) => {
    if (TENANT_RESTRICTED_ROLES.has(roleName)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      roles: prev.roles?.includes(roleName)
        ? prev.roles.filter((r) => r !== roleName)
        : [...(prev.roles || []), roleName],
    }));
  };

  const toggleGroup = (groupId: string) => {
    setFormData((prev) => ({
      ...prev,
      groupIds: prev.groupIds?.includes(groupId)
        ? prev.groupIds.filter((g) => g !== groupId)
        : [...(prev.groupIds || []), groupId],
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Invite User</h3>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="user@company.com"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="John Smith"
              />
            </div>

            <div className="form-group">
              <label>Phone (for SMS)</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+61 400 000 000"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Timezone</label>
            <select
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            >
              {COMMON_TIMEZONES.map((timezone) => (
                <option key={timezone} value={timezone}>{timezone}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Roles *</label>
            <div className="role-checkboxes">
              {availableRoles.map((role) => (
                <label key={role.roleId} className="checkbox-label invite-role-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.roles?.includes(role.name) || false}
                    onChange={() => toggleRole(role.name)}
                    disabled={role.name === 'TenantAdmin'}
                  />
                  <span className="checkbox-label-content">
                    <strong>{role.name}</strong>
                    {role.description && <span className="checkbox-description">{role.description}</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Groups</label>
            <div className="role-checkboxes">
              {availableGroups.map((group) => (
                <label key={group.groupId} className="checkbox-label invite-role-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.groupIds?.includes(group.groupId) || false}
                    onChange={() => toggleGroup(group.groupId)}
                  />
                  <span className="checkbox-label-content">
                    <strong>{group.name}</strong>
                    <span className="checkbox-description">{group.type}</span>
                  </span>
                </label>
              ))}
              {availableGroups.length === 0 && (
                <div className="help-text">No groups found. Create groups first if you want automatic membership.</div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditUserModal: React.FC<{
  user: User;
  onClose: () => void;
  onSave: (userId: string, roles: string[]) => Promise<void>;
}> = ({ user, onClose, onSave }) => {
  const [roles, setRoles] = useState<string[]>(user.roles || []);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const allRoles = await tenantApi.getRoles();
      setAvailableRoles((allRoles || []).filter((r) => !TENANT_RESTRICTED_ROLES.has(r.name)));
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const toggleRole = (roleName: string) => {
    if (TENANT_RESTRICTED_ROLES.has(roleName)) {
      return;
    }

    setRoles((prev) =>
      prev.includes(roleName)
        ? prev.filter((r) => r !== roleName)
        : [...prev, roleName]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSave(user.userId, roles);
    } catch (err: any) {
      const message = err?.message || 'Failed to save roles';
      setError(message);
      console.error(message, err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Edit User: {user.displayName}</h3>
        <p className="modal-subtitle">{user.email}</p>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label>Roles</label>
            <div className="role-checkboxes">
              {availableRoles.map((role) => (
                <label key={role.roleId} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={roles.includes(role.name)}
                    onChange={() => toggleRole(role.name)}
                  />
                  <strong>{role.name}</strong>
                  {role.description && <span>{role.description}</span>}
                </label>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
